import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { buffer, debounceTime, filter, Subject, takeUntil } from 'rxjs';
import { ChangeReason } from '../../../api/core/models/api/change-reason';
import { inventorySaveManagerActions } from '../../../store/inventories/inventories.actions';
import { selectRouteBoardId } from '../../../store/router/router.selectors';
import { BackendInventoryService } from '../../backend/backend-inventory.service';

export interface InventoryUpdate {
  valueId: string;
  value: number;
  oldValue?: number;
  inventoryId: string;
  variableId: string;
  origin?: ChangeReason;
}

export enum InventorySaveStatus {
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
  IDLE = 'IDLE',
  WAITING = 'WAITING',
}

@Injectable({
  providedIn: 'root',
})
export class InventorySaveManagerService implements OnDestroy {
  private _backendInventoryService: BackendInventoryService = inject(BackendInventoryService);
  private _store = inject(Store);

  private _saveQueue: Subject<InventoryUpdate> = new Subject();
  private _saveQueueSizeSignal = signal(0);
  private _savingStatusSignal = signal(InventorySaveStatus.IDLE);
  private _saveBufferTime = 3000;
  private _destroy$ = new Subject<void>();
  private _isDestroyed = false;
  private _hasErrored = false;
  private _erroredValues: InventoryUpdate[] = [];
  private _boardId: string | undefined;
  private _errorSignal = signal<InventoryUpdate[]>([]);

  get savingStatusSignal() {
    return this._savingStatusSignal;
  }

  get saveQueueSizeSignal() {
    return this._saveQueueSizeSignal;
  }

  get errorSignal() {
    return this._errorSignal;
  }

  constructor() {
    this._store
      .select(selectRouteBoardId)
      .pipe(
        takeUntil(this._destroy$),
        filter((b) => b !== null)
      )
      .subscribe((boardId) => {
        this._boardId = boardId;
        this._initializeSaveProcessor();
      });
  }

  ngOnDestroy(): void {
    if (this._saveQueueSizeSignal() === 0) {
      this._finalizeDestruction();
    } else {
      this._isDestroyed = true;
    }
  }

  clearErrors(): void {
    this._erroredValues = [];
    this._errorSignal.set([]);
    this._hasErrored = false;
    this._savingStatusSignal.set(InventorySaveStatus.IDLE);
  }

  queueSave(update: InventoryUpdate): void {
    this._saveQueue.next(update);
    this._savingStatusSignal.set(InventorySaveStatus.WAITING);
    this._updateSaveQueueSizeSignal(1);
  }

  private _finalizeDestruction(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _initializeSaveProcessor(): void {
    this._savingStatusSignal.set(InventorySaveStatus.IDLE);
    this._saveQueue = new Subject();
    this._saveQueue
      .pipe(
        buffer(this._saveQueue.pipe(debounceTime(this._saveBufferTime))),
        filter((updates) => updates.length > 0),
        takeUntil(this._destroy$)
      )
      .subscribe((updates) => {
        this._updateSaveQueueSizeSignal(-updates.length);
        this._processSaves(updates);

        if (this._isDestroyed && this._saveQueueSizeSignal() === 0) {
          this._finalizeDestruction();
        }
      });
  }

  private _handleSaveError(): void {
    this._savingStatusSignal.set(InventorySaveStatus.ERROR);
    this._errorSignal.set(this._erroredValues);
  }

  private _processSaves(updates: InventoryUpdate[]): void {
    this._savingStatusSignal.set(InventorySaveStatus.SAVING);
    if (!this._boardId) {
      return;
    }
    this._backendInventoryService.updateInventoryValues({ boardId: this._boardId, updates }).subscribe({
      next: () => {
        this._store.dispatch(inventorySaveManagerActions.saveInventoryValueSuccess({ values: updates }));
        if (this._saveQueueSizeSignal() === 0) {
          if (!this._hasErrored) {
            this._savingStatusSignal.set(InventorySaveStatus.SAVED);
          } else {
            this._handleSaveError();
          }
        }
      },
      error: (err) => {
        this._erroredValues.push(...updates);
        if (this._saveQueueSizeSignal() === 0) {
          this._handleSaveError();
        }
        this._hasErrored = true;
        console.error(`Error saving inventory values. InventoryId: ${updates?.[0]?.inventoryId ?? 'unknown'}, variableId: ${updates?.[0]?.variableId ?? 'unknown'}`, err);
      },
    });
  }

  private _updateSaveQueueSizeSignal(delta: number): void {
    const newSize = this._saveQueueSizeSignal() + delta;
    this._saveQueueSizeSignal.set(newSize);
  }
}
